"use client";

import { useEffect } from "react";

import { recordInterest, trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// Fires a high-intent page-view event for service/industry pages + persists
// the slug as a long-lived user property so we can build cohort reports
// like "leads from healthcare-interested visitors" in GA4 later.
//
// Mounted on the page itself (server component), with kind + slug passed
// from the route segment params. Pageviews themselves are still captured
// by GA4's automatic Enhanced Measurement — this event adds the structured
// ecommerce-shape `view_item` so the contact form can fire a matching
// `begin_checkout` and `purchase` to form a complete funnel.
// ----------------------------------------------------------------------------

export type IPageViewTrackerProps = {
  kind: "service" | "industry";
  slug: string;
  name?: string;
};

const PageViewTracker = ({ kind, slug, name }: IPageViewTrackerProps) => {
  useEffect(() => {
    if (!slug) return;
    // Persist the interest so we can segment cohorts in GA4.
    recordInterest(kind, slug);
    // High-intent surface-specific event.
    trackEvent(
      kind === "service" ? "view_service_item" : "view_industry_item",
      { item_id: slug, item_name: name || slug },
    );
    // GA4 ecommerce alias — pairs with begin_checkout (contact form open)
    // and purchase (form submit) to unlock the built-in funnel report.
    trackEvent("view_item", {
      item_id: slug,
      item_name: name || slug,
      item_category: kind,
    });
  }, [kind, slug, name]);

  return null;
};

export default PageViewTracker;
